import { createSlice } from "@reduxjs/toolkit";

const initialState = {
  step1: {
    name: "",
    restaurant_type_id: null,
    country_id: "",
    city_id: null,
    phone: "",
    address: "",
    table_count: 5,
    seating_capacity: 10,
    floor_number: 1,
  },

  step2: {
    short_summary: "",
    detailed_description: "",
    key_points: [],
    meal_type: [],
    has_service: false,
    restaurant_service: [],
  },

  step3: {
    start_date: "",
    end_date: "",
    cancellation_policy_id: null,
    booking_confirmation_mode: "",
    schedules: [],
    dishes: [
      {
        meal_type_id: null,
        dish_name: "",
        dish_description: "",
        dish_price: 0,
      },
    ],
  },
};

function deepUpdate(obj, field, value) {
  if (obj && typeof obj === "object") {
    if (field in obj) {
      obj[field] = value;
      return true;
    }
    for (const key of Object.keys(obj)) {
      if (deepUpdate(obj[key], field, value)) {
        return true;
      }
    }
  }
  return false;
}

const restaurantSlice = createSlice({
  name: "restaurant",
  initialState,
  reducers: {
    updateRestaurantStepField: (state, action) => {
      const { field, value } = action.payload;

      if (typeof field !== "string") {
        console.error("updateRestaurantStepField: invalid field", field);
        return;
      }

      // Dot notation support
      if (field.includes(".")) {
        const keys = field.split(".");
        let target = state;
        for (let i = 0; i < keys.length - 1; i++) {
          if (!target[keys[i]]) return;
          target = target[keys[i]];
        }
        target[keys[keys.length - 1]] = value;
        return;
      }

      // Deep search fallback
      if (!deepUpdate(state, field, value)) {
        console.warn(`Field '${field}' not found in state`);
      }
    },

    updateRestaurantStepBulk: (state, action) => {
      const { step, data } = action.payload;
      state[step] = { ...state[step], ...data };
    },

    setRestaurantProductId: (state, action) => {
      state.product_id = action.payload;
      state.step2.product_id = action.payload;
      state.step3.product_id = action.payload;
    },

    // ============================
    // Dish Management
    // ============================
    addDish: (state, action) => {
      const newDish = {
        meal_type_id: null,
        dish_name: "",
        dish_description: "",
        dish_price: 0,
        ...action.payload,
      };
      state.step3.dishes.push(newDish);
    },

    updateDish: (state, action) => {
      const { index, data } = action.payload;
      if (state.step3.dishes[index]) {
        state.step3.dishes[index] = {
          ...state.step3.dishes[index],
          ...data,
        };
      }
    },

    updateDishField: (state, action) => {
      const { index, field, value } = action.payload;
      if (!state.step3.dishes[index]) return;

      if (field.includes(".")) {
        const keys = field.split(".");
        let target = state.step3.dishes[index];
        for (let i = 0; i < keys.length - 1; i++) {
          if (!target[keys[i]]) return;
          target = target[keys[i]];
        }
        target[keys[keys.length - 1]] = value;
      } else {
        state.step3.dishes[index][field] = value;
      }
    },

    deleteDish: (state, action) => {
      const { index } = action.payload;
      if (index >= 0 && index < state.step3.dishes.length) {
        state.step3.dishes.splice(index, 1);
      }
    },

    // ============================
    // Schedule Management
    // ============================
    addSchedule: (state, action) => {
      const newSchedule = {
        day_of_week: "",
        start_time: "",
        end_time: "",
        is_open_24h: false,
        is_closed: false,
        ...action.payload,
      };
      state.step3.schedules.push(newSchedule);
    },

    updateSchedule: (state, action) => {
      const { index, data } = action.payload;
      if (state.step3.schedules[index]) {
        state.step3.schedules[index] = {
          ...state.step3.schedules[index],
          ...data,
        };
      }
    },

    updateScheduleField: (state, action) => {
      const { index, field, value } = action.payload;
      if (!state.step3.schedules[index]) return;

      if (field.includes(".")) {
        const keys = field.split(".");
        let target = state.step3.schedules[index];
        for (let i = 0; i < keys.length - 1; i++) {
          if (!target[keys[i]]) return;
          target = target[keys[i]];
        }
        target[keys[keys.length - 1]] = value;
      } else {
        state.step3.schedules[index][field] = value;
      }
    },

    deleteSchedule: (state, action) => {
      const { index } = action.payload;
      if (index >= 0 && index < state.step3.schedules.length) {
        state.step3.schedules.splice(index, 1);
      }
    },

    // ============================
    // Service Management
    // ============================
    toggleRestaurantService: (state, action) => {
      const { serviceId } = action.payload;
      const index = state.step2.restaurant_service.findIndex(
        (id) => id === serviceId
      );

      if (index > -1) {
        state.step2.restaurant_service.splice(index, 1);
      } else {
        state.step2.restaurant_service.push(serviceId);
      }
    },

    toggleMealType: (state, action) => {
      const { mealTypeId } = action.payload;
      const index = state.step2.meal_type.findIndex((id) => id === mealTypeId);

      if (index > -1) {
        state.step2.meal_type.splice(index, 1);
      } else {
        state.step2.meal_type.push(mealTypeId);
      }
    },

    resetRestaurant: () => initialState,
  },
});

export const {
  updateRestaurantStepField,
  updateRestaurantStepBulk,
  setRestaurantProductId,
  addDish,
  updateDish,
  updateDishField,
  deleteDish,
  addSchedule,
  updateSchedule,
  updateScheduleField,
  deleteSchedule,
  toggleRestaurantService,
  toggleMealType,
  resetRestaurant,
} = restaurantSlice.actions;

export default restaurantSlice.reducer;
